import request from '@apps-common/js-client/api/request';
import {ProductAccessStates} from '@account/constants/products';
import {
  PDA_ADMIN_V1_BASE_PATH as base,
  IMS_BASE_PATH as imsBase,
} from '@account/config';

export const getProductAccesses = ({
  limit = 15,
  offset = 0,
  sort = 'title',
  descending = false,
  state = [ProductAccessStates.ACTIVE],
  customerAccountId,
  orgId,
  search,
  workspaceId,
} = {}) => {
  // Build search parameters based on search term
  const params = {
    limit,
    offset,
    sort: `${descending ? '-' : ''}${sort}`,
    state__in: state,
    customer_account_id: customerAccountId,
    organization_id: orgId,
    // null → send "null" string so backend filters workspace_id IS NULL (CA-level only)
    // undefined → omit entirely (no filter, returns all)
    ...(workspaceId !== undefined && {workspace_id: workspaceId ?? 'null'}),
  };

  // Handle search logic: if search term starts with "pl-" (case-insensitive),
  // use pl_number__icontains, otherwise use generic search
  if (search) {
    const trimmedSearch = search.trim().toLowerCase();
    if (trimmedSearch.startsWith('pl-')) {
      params.pl_number__icontains = search.trim(); // Use original casing for the actual search
    } else {
      params.search = search;
    }
  }

  return request.get(`${base}/product-access/`, {params}).then(res => res.data);
};

export const getProductAccess = productAccessId =>
  request
    .get(`${base}/product-access/${productAccessId}`)
    .then(res => res.data);

export const getProductAccessLedgerEntries = (
  productAccessId,
  {limit = 25, offset = 0, productAccessTermId, validFrom, validTo} = {}
) => {
  const params = {limit, offset, grouped: true, sort: 'created_at'};
  // Scope the grouped ledger to a term window. product_access_term_id and
  // valid_from/valid_to are mutually exclusive on the backend (400 if both);
  // prefer the term id (backend resolves the dates, amendment-safe).
  if (productAccessTermId !== undefined && productAccessTermId !== null) {
    params.product_access_term_id = productAccessTermId;
  } else {
    if (validFrom !== undefined && validFrom !== null)
      params.valid_from = validFrom;
    if (validTo !== undefined && validTo !== null) params.valid_to = validTo;
  }
  return request
    .get(`${base}/product-access/${productAccessId}/ledger-entries`, {params})
    .then(res => res.data);
};

export const allocateProductAccess = (productAccessId, data) =>
  request
    .post(`${base}/product-access/${productAccessId}/allocate`, data)
    .then(res => res.data);

export const deactivateProductAccess = productAccessId =>
  request
    .delete(`${base}/product-access/${productAccessId}`)
    .then(res => res.data);

export const getProductAccessUsage = productAccessId =>
  request
    .get(`${base}/product-access/${productAccessId}/quota-usage`)
    .then(res => res.data);

export const getAllProductVariants = ({
  sort = 'title',
  descending = false,
} = {}) =>
  request
    .get(`${base}/products/`, {
      params: {
        limit: 10000,
        sort: `${descending ? '-' : ''}${sort}`,
      },
    })
    .then(res => res.data);

export const getProductVariants = ({
  limit = 1000, // Large limit to get all variants in one call
  offset = 0,
  sort = 'title',
  descending = false,
} = {}) =>
  request
    .get(`${base}/products/`, {
      params: {
        limit,
        offset,
        sort: `${descending ? '-' : ''}${sort}`,
      },
    })
    .then(res => res.data);

export const getProductAccessTerms = productAccessId =>
  request
    .get(`${base}/product-access/${productAccessId}/terms`)
    .then(res => res.data);

export const addProductAccessTerm = (productAccessId, body) =>
  request
    .post(`${base}/product-access/${productAccessId}/terms/`, body)
    .then(res => res.data);

export const adjustTermQuota = (productAccessId, patId, body) =>
  request
    .post(
      `${base}/product-access/${productAccessId}/terms/${patId}/adjust-quota`,
      body
    )
    .then(res => res.data);

// Usage Reversal: signed usage adjustment against the Usage API bucket backing a
// ProductAccess. `amount` may be negative to decrement recorded usage. Requires
// pl_number + support_ticket_ref for the accounting paper trail.
//
// Disable axios-retry for this endpoint. Its default retryCondition
// (isNetworkOrIdempotentRequestError) replays POSTs on a network error — i.e.
// the exact case where the server committed the adjustment but the response was
// dropped in transit — which would double-apply a financial write. React Query
// mutations don't retry by default, so fail fast at the HTTP layer instead.
export const adjustProductAccessUsage = (
  productAccessId,
  body,
  idempotencyKey
) =>
  request
    .post(`${base}/product-access/${productAccessId}/adjustments`, body, {
      // Required by the backend, which claims the key before calling the Usage
      // API so a retry with the same key replays instead of double-applying.
      headers: {'Idempotency-Key': idempotencyKey},
      'axios-retry': {retries: 0},
    })
    .then(res => res.data);

export const extendTermDates = (productAccessId, patId, body) =>
  request
    .post(
      `${base}/product-access/${productAccessId}/terms/${patId}/extend`,
      body
    )
    .then(res => res.data);

export const deleteProductAccessTerm = (
  productAccessId,
  patId,
  {supportTicketRef} = {}
) =>
  request
    .delete(`${base}/product-access/${productAccessId}/terms/${patId}/`, {
      data: {support_ticket_ref: supportTicketRef},
      headers: {'Content-Type': 'application/json'},
    })
    .then(res => res.data);

export const getTermBalance = (productAccessId, patId) =>
  request
    .get(`${base}/product-access/${productAccessId}/terms/${patId}/balance`)
    .then(res => res.data);

export const bulkRecallWorkspaces = ({
  workspacePaIds,
  supportTicketRef,
  notes,
}) =>
  request
    .post(`${base}/product-access/bulk-recall/`, {
      workspace_pa_ids: workspacePaIds,
      support_ticket_ref: supportTicketRef,
      notes,
    })
    .then(res => res.data);

export const PA_BULK_LIMIT = 500;

// Returns all workspaces for a CA via IMS.
// Each workspace has {workspace_id, name, enabled} sorted by name.
const WORKSPACE_PAGE_SIZE = 100;
export const getWorkspacesForCA = customerAccountId =>
  request
    .get(`${imsBase}/workspaces`, {
      params: {accountId: customerAccountId, count: WORKSPACE_PAGE_SIZE},
    })
    .then(res => {
      const member = res.data?.member ?? [];
      const workspaces = member
        .map(ws => ({
          workspace_id: ws.id,
          name: ws.name || 'Unnamed Workspace',
          enabled: ws.enabled !== false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      return {workspaces, truncated: member.length >= WORKSPACE_PAGE_SIZE};
    });
